import { Button } from '@chakra-ui/button'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  HStack,
  Link,
  PinInput,
  PinInputField,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import useToast from '../hooks/useToast'

export type ElevationOperation =
  | 'reveal'
  | 'roll'
  | 'delete'
  | 'create_restricted'
  | 'update_restricted'

interface Props {
  isOpen: boolean
  operation: ElevationOperation
  clientNumber?: string
  onVerified(): void
  onCancel(): void
}

const OTP_LENGTH = 6

const OtpVerificationModal = ({
  isOpen,
  operation,
  clientNumber,
  onVerified,
  onCancel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [code, setCode] = useState<string>('')
  const [error, setError] = useState<string>('')

  // Dedicated fetch instance so verify/resend state never races with the
  // provider's initiate request.
  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  // Reset transient state whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setCode('')
      setError('')
    }
  }, [isOpen])

  const operationLabel: Record<ElevationOperation, string> = {
    reveal: t('op_reveal_api_key', 'reveal your API keys'),
    roll: t('op_roll_api_key', 'roll your API keys'),
    delete: t('op_delete_api_key', 'delete your API keys'),
    create_restricted: t(
      'op_create_restricted_api_key',
      'create a restricted API key',
    ),
    update_restricted: t(
      'op_update_restricted_api_key',
      'update restricted API key permissions',
    ),
  }

  const verify = async (value: string): Promise<void> => {
    if (!clientNumber || value.length !== OTP_LENGTH) return
    setError('')
    const response = (await request.post(
      `/api/clients/${clientNumber}/settings/developer/elevation/verify`,
      { code: value, operation },
    )) as { elevated?: boolean; message?: string }

    if (request.error) {
      setCode('')
      setError(
        response?.message ??
          t('invalid_verification_code', 'The verification code is incorrect.'),
      )
    } else {
      onVerified()
    }
  }

  const resend = async (): Promise<void> => {
    if (!clientNumber) return
    setError('')
    const response = (await request.post(
      `/api/clients/${clientNumber}/settings/developer/elevation/resend`,
      { operation },
    )) as { message?: string }
    if (request.error) {
      setError(
        response?.message ??
          t(
            'unable_to_resend_code',
            'Unable to resend the code. Please try again.',
          ),
      )
    } else {
      toast({
        description: t(
          'verification_code_resent',
          'A new verification code has been sent to your email.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="md" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('verify_its_you', 'Verify it’s you')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error ? (
            <Alert status="error" mb="1em" minWidth={0}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <VStack spacing={4} alignItems="flex-start">
            <Text>
              {t(
                'elevation_otp_description',
                'To {{action}}, enter the 6-digit verification code we emailed you.',
                { action: operationLabel[operation] },
              )}
            </Text>
            <HStack>
              <PinInput
                otp
                type="number"
                value={code}
                isInvalid={Boolean(error)}
                onChange={value => {
                  setCode(value)
                  if (error) setError('')
                }}
                onComplete={value => {
                  void verify(value)
                }}
                isDisabled={request.loading}
              >
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
            <Text fontSize="sm" color="appSlate">
              {t('did_not_receive_code', 'Didn’t receive the code?')}{' '}
              <Link
                as="button"
                type="button"
                color="appBlue"
                onClick={() => {
                  void resend()
                }}
              >
                {t('resend', 'Resend')}
              </Link>
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
          <Button variant="outline" onClick={onCancel}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            isDisabled={code.length !== OTP_LENGTH}
            isLoading={request.loading}
            onClick={() => {
              void verify(code)
            }}
          >
            {t('verify', 'Verify')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default OtpVerificationModal
